/**
 * Shared coupon UI helpers (user-facing formatting + error picking).
 *
 * Keep this file framework-agnostic so it can be used from different entry points
 * (Subscription Settings, Upgrade modal, etc).
 */

export const getFriendlyCouponErrorMessage = (raw) => {
  const msg = String(raw || "").trim();
  const lower = msg.toLowerCase();

  if (!msg) return "We couldn’t apply that coupon. Please try again.";
  if (lower.includes("invalid coupon code")) {
    return "We couldn’t find that coupon code. Double-check it and try again.";
  }
  if (lower.includes("not available")) {
    return "That coupon has expired or was deactivated.";
  }
  if (lower.includes("only valid for first-time")) {
    return "That coupon is only valid for first-time subscribers.";
  }
  if (lower.includes("not valid for this billing period")) {
    return "That coupon doesn’t apply to this billing period.";
  }
  if (lower.includes("not valid for this plan")) {
    return "That coupon doesn’t apply to this plan.";
  }
  return msg;
};

export const pickBestCouponError = (...errors) => {
  // Prioritize “expired/deactivated” over “not found” when multiple responses disagree.
  const normalized = (errors || []).map((e) => String(e || "").trim()).filter(Boolean);
  if (normalized.length === 0) return null;

  const lower = normalized.map((e) => e.toLowerCase());
  const idx = (predicate) => lower.findIndex(predicate);

  const iNotAvailable = idx((s) => s.includes("not available"));
  if (iNotAvailable !== -1) return normalized[iNotAvailable];

  const iFirstTime = idx((s) => s.includes("only valid for first-time"));
  if (iFirstTime !== -1) return normalized[iFirstTime];

  const iBilling = idx((s) => s.includes("not valid for this billing period"));
  if (iBilling !== -1) return normalized[iBilling];

  const iPlan = idx((s) => s.includes("not valid for this plan"));
  if (iPlan !== -1) return normalized[iPlan];

  const iInvalid = idx((s) => s.includes("invalid coupon code"));
  if (iInvalid !== -1) return normalized[iInvalid];

  return normalized[0];
};

export const formatFreeTrialLabel = (daysRaw) => {
  const days = Number(daysRaw || 0);
  if (!Number.isFinite(days) || days <= 0) return null;

  // Rule:
  // - If days < 29 and multiple of 7 -> show weeks
  // - If multiple of 30 -> show months
  // - Otherwise -> show days
  if (days < 29 && days % 7 === 0) {
    const weeks = days / 7;
    return `Free trial: ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  }
  if (days % 30 === 0) {
    const months = days / 30;
    return `Free trial: ${months} ${months === 1 ? "month" : "months"}`;
  }
  return `Free trial: ${days} ${days === 1 ? "day" : "days"}`;
};

export const buildCouponAppliedLabel = ({ code, hasMonthly, hasYearly, freeTrialDays }) => {
  const c = String(code || "").trim();
  if (!c) return null;
  const periodLabel = hasMonthly && hasYearly ? "monthly + annual" : hasMonthly ? "monthly" : hasYearly ? "annual" : "";
  const trial = freeTrialDays ? ` · ${formatFreeTrialLabel(freeTrialDays)}` : "";
  return `Applied ${c} · ${periodLabel}${trial}`;
};

const roundMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v * 100) / 100;
};

const inferDiscountFromPlans = (plans = []) => {
  const rows = (plans || [])
    .map((p) => ({
      base: Number(p?.base_price),
      discounted: Number(p?.discounted_price),
    }))
    .filter((p) => Number.isFinite(p.base) && Number.isFinite(p.discounted) && p.base > 0 && p.discounted >= 0 && p.discounted < p.base);

  if (rows.length === 0) return { percent_off: null, amount_off: null };

  const diffs = rows.map((r) => r.base - r.discounted);
  const pcts = rows.map((r) => ((r.base - r.discounted) / r.base) * 100);

  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / Math.max(1, arr.length);

  // Prefer percent-off when it looks like a clean integer percent across plans.
  const pctAvg = avg(pcts);
  const pctRounded = Math.round(pctAvg);
  const pctLooksInteger = Math.abs(pctAvg - pctRounded) < 0.25 && pcts.every((p) => Math.abs(p - pctRounded) < 0.5);
  if (pctLooksInteger && pctRounded > 0) return { percent_off: pctRounded, amount_off: null };

  // Otherwise, attempt amount-off when it looks consistent across plans.
  const diffAvg = avg(diffs);
  const diffRounded = roundMoney(diffAvg);
  const diffConsistent = diffRounded != null && diffs.every((d) => Math.abs(d - diffRounded) < 0.1);
  if (diffConsistent && diffRounded > 0) return { percent_off: null, amount_off: diffRounded };

  return { percent_off: null, amount_off: null };
};

/**
 * Normalize coupon preview responses into the shared couponState shape.
 * Also infers percent_off/amount_off from plan previews when backend omits those fields.
 */
export const buildCouponStateFromPreview = ({ monthly, yearly }) => {
  const monthlyValid = !!monthly?.valid && !!monthly?.data;
  const yearlyValid = !!yearly?.valid && !!yearly?.data;
  const canonical = (monthlyValid ? monthly.data : null) || (yearlyValid ? yearly.data : null) || {};

  const mergedPlans = [
    ...(monthlyValid ? monthly.data.plans || [] : []),
    ...(yearlyValid ? yearly.data.plans || [] : []),
  ];
  const inferred = inferDiscountFromPlans(mergedPlans);

  const percent_off = canonical.percent_off != null ? Number(canonical.percent_off) : inferred.percent_off;
  const amount_off = canonical.amount_off != null ? Number(canonical.amount_off) : inferred.amount_off;

  return {
    code: canonical.code,
    include_renewals: canonical.include_renewals,
    percent_off: percent_off != null && Number.isFinite(Number(percent_off)) ? Number(percent_off) : null,
    amount_off: amount_off != null && Number.isFinite(Number(amount_off)) ? Number(amount_off) : null,
    currency: canonical.currency || null,
    free_trial_days:
      canonical.free_trial_days != null ? canonical.free_trial_days : Number(canonical.additional_months || 0) * 30,
    byPeriod: {
      monthly: monthlyValid ? { plans: monthly.data.plans || [] } : null,
      yearly: yearlyValid ? { plans: yearly.data.plans || [] } : null,
    },
  };
};


